import { BodyCopy } from "./BodyCopy";
import { Button } from "lola-framework-ui-test";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { LimitInfoIcon } from "../assets/icons";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { accentInkOnSurface } from "../utils/accentInkOnSurface";
import { TITLE_COLOR } from "../constants";
import { countryDisplayName } from "../utils/country-display";

export interface RouteQuoteUnavailableSheetProps {
  destinationCountry: string;
  payerName?: string;
  canReturnToChat: boolean;
  closing?: boolean;
  onChooseMethod: () => void;
  onReturnToChat: () => void;
}

export const RouteQuoteUnavailableSheet: React.FC<
  RouteQuoteUnavailableSheetProps
> = ({
  destinationCountry,
  payerName,
  canReturnToChat,
  closing,
  onChooseMethod,
  onReturnToChat,
}) => {
  const { t, i18n } = useTranslation();
  const { theme } = useStore();
  const { bodyCopy, background, backgroundBtn, textColorBtn } =
    theme.whiteView;
  const partnerColors = useSessionStore(
    (s) => s.session.data?.partner?.theme?.colors,
  ) as Record<string, string> | undefined;
  const accentSolid = accentInkOnSurface(partnerColors, background, bodyCopy);

  const countryName = useMemo(
    () => countryDisplayName(destinationCountry, i18n.language || "en"),
    [destinationCountry, i18n.language],
  );
  const payer = payerName?.trim() ?? "";

  return (
    <section className="route-quote-sheet">
      <LimitInfoIcon size={60} colors={[accentSolid, accentSolid]} />
      <h2 className="route-quote-sheet__title" style={{ color: TITLE_COLOR }}>
        {t("routeUnavailable.methodDrawerTitle")}
      </h2>
      <BodyCopy className="route-quote-sheet__text" textColor={bodyCopy}>
        {payer
          ? t("routeUnavailable.methodDrawerMessagePayer", {
              payer,
              country: countryName,
            })
          : t("routeUnavailable.methodDrawerMessage", { country: countryName })}
      </BodyCopy>
      <div className="route-quote-sheet__actions">
        {canReturnToChat ? (
          <Button
            variant="cancel"
            size="large"
            color={bodyCopy}
            disabled={closing}
            onClick={onReturnToChat}
          >
            {t("routeUnavailable.backToChat")}
          </Button>
        ) : null}
        <Button
          size="large"
          background={backgroundBtn}
          color={textColorBtn}
          onClick={onChooseMethod}
        >
          {t("routeUnavailable.chooseAnotherMethod")}
        </Button>
      </div>
    </section>
  );
};
